/**
 * SOAP改善提案APIクライアント
 */
import client from '@/shared/apis/client'
import { camelCase } from '@/shared/utils/caseConverter'
import { logError } from '@/shared/utils/errorHandling'
import {
  SoapImprovementSuggestion,
  ApplySuggestionResponse,
  DismissSuggestionResponse,
} from '@/apps/latest/features/formatter/types/soapImprovement'

/**
 * APIレスポンスの型ガード
 */
const isSoapImprovementSuggestion = (
  item: unknown
): item is Record<string, unknown> => {
  return (
    typeof item === 'object' &&
    item !== null &&
    'id' in item &&
    'event' in item &&
    'label' in item
  )
}

/**
 * レスポンスの型変換とバリデーション
 */
const validateAndTransformSuggestion = (
  item: unknown
): SoapImprovementSuggestion | null => {
  if (!isSoapImprovementSuggestion(item)) {
    logError(
      'soapImprovement.validateAndTransformSuggestion',
      new Error('Invalid suggestion format')
    )
    return null
  }

  try {
    return camelCase<SoapImprovementSuggestion>(item)
  } catch (error) {
    logError('soapImprovement.validateAndTransformSuggestion', error)
    return null
  }
}

export const soapImprovementAPI = {
  /**
   * 改善提案一覧の取得
   * @param yakurekiId 薬歴ID
   * @returns 改善提案のリスト
   */
  getSuggestions: async (
    yakurekiId: string
  ): Promise<SoapImprovementSuggestion[]> => {
    const response = await client.get<unknown[]>(
      `/api/yakureki/${yakurekiId}/improvement-suggestions/`
    )

    // 型安全な変換とバリデーション
    if (!Array.isArray(response.data)) {
      logError(
        'soapImprovement.getSuggestions',
        new Error('Invalid response format: expected array')
      )
      return []
    }

    const validSuggestions = response.data
      .map(validateAndTransformSuggestion)
      .filter((item): item is SoapImprovementSuggestion => item !== null)

    return validSuggestions
  },

  /**
   * 改善提案の適用
   * @param yakurekiId 薬歴ID
   * @param suggestionId 提案ID
   * @returns 適用結果
   */
  applySuggestion: async (
    yakurekiId: string,
    suggestionId: string
  ): Promise<ApplySuggestionResponse> => {
    const response = await client.post<unknown>(
      `/api/yakureki/${yakurekiId}/apply-suggestion/`,
      { suggestion_id: suggestionId }
    )

    // 型安全な変換
    if (typeof response.data !== 'object' || response.data === null) {
      throw new Error('Invalid response format')
    }

    return camelCase<ApplySuggestionResponse>(response.data)
  },

  /**
   * 改善提案の却下
   * @param yakurekiId 薬歴ID
   * @param suggestionId 提案ID
   * @returns 却下結果
   */
  dismissSuggestion: async (
    yakurekiId: string,
    suggestionId: string
  ): Promise<DismissSuggestionResponse> => {
    const response = await client.post<DismissSuggestionResponse>(
      `/api/yakureki/${yakurekiId}/dismiss-suggestion/`,
      { suggestion_id: suggestionId }
    )

    // 型安全な検証
    if (typeof response.data !== 'object' || response.data === null) {
      throw new Error('Invalid response format')
    }

    if (!('success' in response.data)) {
      throw new Error('Missing required field: success')
    }

    return response.data
  },
}
